
import { computed, defineComponent, PropType, reactive } from 'vue'

// components
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// realisation
import { orders } from '@/core/realisations/orders.realisation'

// types
import { InvoiceT } from '@/core/types/orders.types'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

import { createToast } from 'mosha-vue-toastify'
import { useRouter } from 'vue-router'

export default defineComponent({
    components: { ButtonDropdown, TheButton },
    props: {
        invoice: {
            type: Object as PropType<InvoiceT>,
            default: null,
        },
        deliveryHistory: {
            type: Array as PropType<any[]>,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        boxLoading: {
            type: Boolean,
            default: false,
        },
        restoreLoading: {
            type: Boolean,
            default: false,
        },
        waitListLoading: {
            type: Boolean,
            default: false,
        },
        partialReturn: {
            type: Boolean,
            default: false,
        },
        propertiesLoading: {
            type: Boolean,
            default: false,
        }
    },
    emits: [
        'declared-price',
        'change-invoice-number',
        'print',
        'restore',
        'verify',
        'previous-status',
        'save-box',
        'wait-list',
        'predict-date',
        'status-resended-to-one-c',
        'approve-delivery',
        'properties',
        'arrested'
    ],
    setup(props, { emit }) {
        const { havePermission, havePermissions } = useAccount()
        const router = useRouter()
        const notifications = useNotifications()

        const state = reactive({
            is_cancelling: false,
            is_dk_loading: false,
            is_resending_to_one_c: false,
            is_arresting: false,
        })
        //

        const cancelInvoice = async () => {
            const answer = confirm(
                `Вы действительно хотите отменить накладную (id: ${props.invoice.id})?`
            )
            if (!answer) return

            try {
                state.is_cancelling = true

                await orders.cancelInvoice(props.invoice.id)
                await router.replace({ name: 'InvoicesHistory' })
            } catch (error) {
                console.error(error)

                createToast(
                    {
                        title: 'Произошла ошибка при отмене накладной',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                state.is_cancelling = false
            }
        }

        // const approveSelfDeliery = async () => {
        //     try {
        //         state.is_dk_loading = true

        //         await orders.approveSelfDelivery(props.invoice.id)
        //     } catch (error) {
        //         console.error(error)
        //         notifications.notifyError({
        //             error,
        //             title: 'Произошла ошибка',
        //         })
        //     } finally {
        //         state.is_dk_loading = false
        //     }
        // }

        const resendToOneC = async () => {
            const answer = confirm('Вы действительно хотите переотправить статус доставки в 1С?')
            if (!answer) return

            try {
                state.is_resending_to_one_c = true

                await orders.resendInvoiceDeliveryStatusToOneC(props.invoice.id)

                notifications.notify({
                    title: 'Статус доставки переотправлен в 1С',
                    type: 'success',
                })
                emit('status-resended-to-one-c')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При переотправке статуса доставки в 1С произошла ошибка',
                })
            } finally {
                state.is_resending_to_one_c = false
            }
        }

        const arrestInvoice = async () => {
            const answer = confirm(`Вы действительно хотите блокировать выдачу?`)
            if (!answer) return

            try {
                state.is_arresting = true

                await orders.arrestInvoice(props.invoice.id, { isArrest: true })
                emit('arrested')
            } catch (error) {
                console.error(error)

                createToast(
                    {
                        title: 'Произошла ошибка при блокировке выдачи',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                state.is_arresting = false
            }
        }

        const unarrestInvoice = async () => {
            const answer = confirm(`Вы действительно хотите разблокировать выдачу?`)
            if (!answer) return

            try {
                state.is_arresting = true

                await orders.arrestInvoice(props.invoice.id, { isArrest: false })
                emit('arrested')
            } catch (error) {
                console.error(error)

                createToast(
                    {
                        title: 'Произошла ошибка при разблокировке выдачи',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                state.is_arresting = false
            }
        }

        // emits

        const emitDeclaredPrice = () => emit('declared-price')
        const emitVerify = () => emit('verify')
        const emitPreviousStatus = () => emit('previous-status')
        const emitChangeInvoiceNumber = () => emit('change-invoice-number')
        const emitPrintInvoice = () => emit('print')
        const emitRestore = () => emit('restore')
        const emitWaitList = () => emit('wait-list')
        const emitSaveBox = () => emit('save-box')
        const emitPredictDate = () => emit('predict-date')
        const emitApproveDelivery = () => emit('approve-delivery')
        const emitProperties = () => emit('properties')

        // computed

        const isOnBeforeHandling = computed(() => {
            if (!props.invoice) return false
            if (props.deliveryHistory.length === 0) return false

            if (
                props.deliveryHistory.some(
                    (status) =>
                        (status.status_code === 201 ||
                            status.status_code === 202 ||
                            status.status_code === 203 ||
                            status.status_code === 225) &&
                        status.date
                )
            ) {
                return true
            }

            return false
        })

        const isOnBeforeGivingOnDelivery = computed(() => {
            if (!props.invoice) return false
            if (props.deliveryHistory.length === 0) return false

            if (
                props.deliveryHistory.some(
                    (status) =>
                        (status.status_code === 201 ||
                            status.status_code === 202 ||
                            status.status_code === 203 ||
                            status.status_code === 225 ||
                            status.status_code === 205 ||
                            status.status_code === 206 ||
                            status.status_code === 208) &&
                        status.date
                )
            ) {
                return true
            }

            return false
        })

        const isWaiting = computed(() => {
            const waitStatus = props.deliveryHistory.find((status) => status.status_code === 205)
            return Boolean(waitStatus && waitStatus.date)
        })

        const isCanUpdateInvoice = computed(() => {
            if (!props.invoice) return false

            if (
                havePermission('all-root-permission') ||
                havePermission('order-logistic-info-update')
            )
                return true

            if (isOnBeforeHandling.value || isOnBeforeGivingOnDelivery.value) {
                return true
            }

            return false
        })

        const isCanCancelInvoice = computed(() => {
            if (
                props.invoice &&
                props.invoice.status &&
                ![204, 503].includes(props.invoice.status.code)
            ) {
                // Тех. поддержка
                if (havePermission('all-root-permission')) {
                    return true
                }

                if (
                    props.invoice.type !== 1 &&
                    (props.invoice.status.code === 201 || props.invoice.status.code === 202)
                ) {
                    return true
                }
            }

            return false
        })

        const isCanUpdateInvoiceNumber = computed(() => {
            if (
                havePermissions([
                    'order-logistics-info-change-invoice-number',
                    'all-root-permission',
                ])
            )
                return true
            return false
        })

        const isPredictDate = computed(() =>
            havePermissions(['cabinet.set-new-predict-date', 'all-root-permission'])
        )

        const isWaitList = computed(
            () =>
                havePermissions(['cabinet.store-wait-list-status', 'all-root-permission']) &&
                props.invoice &&
                props.invoice.status &&
                props.invoice.status.id === 6
        )

        const isRestore = computed(() => {
            return (
                props.invoice &&
                props.invoice.status &&
                [204, 503].includes(props.invoice.status.code)
            )
        })

        const getCancelBtnText = computed(() => {
            let status_203 = props.deliveryHistory.find((status) => status.status_code === 203)
            if (status_203 && status_203.date) return 'Отмена доставки'

            return 'Отмена забора'
        })

        const isCanResendToOneC = computed(() => {
            if (props.invoice && props.invoice.status) {
                return (
                    props.invoice.status.code === 211 &&
                    havePermissions([
                        'dispatcher.resend-delivery-status-onec',
                        'all-root-permission',
                    ])
                )
            }

            return false
        })

        const isVerify = computed(() => havePermission('order-logistics-info-update-verification'))

        //

        return {
            state,
            //
            cancelInvoice,
            resendToOneC,
            arrestInvoice,
            unarrestInvoice,
            //
            emitDeclaredPrice,
            emitVerify,
            emitPreviousStatus,
            emitChangeInvoiceNumber,
            emitPredictDate,
            emitApproveDelivery,
            emitPrintInvoice,
            emitRestore,
            emitWaitList,
            emitSaveBox,
            emitProperties,
            havePermissions,
            //
            isCanUpdateInvoice,
            isCanUpdateInvoiceNumber,
            isPredictDate,
            isWaitList,
            isCanCancelInvoice,
            // approveSelfDeliery,
            isRestore,
            getCancelBtnText,
            isCanResendToOneC,
            isVerify,
            isWaiting,
        }
    },
})
